import React, { useState } from "react"
import { Button, Collapse } from "reactstrap"
import {
    DecisionBadge,
    MILO_STATUSES,
    formatDial,
    formatLocalTime,
    formatMoney,
    formatRatio,
    formatShare,
    getMiloStatus,
    getReasonText,
} from "./milo_helper"

// Staff asked to see the name MILO everywhere, never the vendor model behind it (Jev), and to hide
// confidence. The backend writes "Jev" into model ids, notes and error text, and "(confidence 0.68)"
// into the model note. Old rows keep that text, so it is cleaned here instead of in the backend.
const MODEL_NAMES = { jev: "MILO", simple_rule: "Simple rule" }
const modelName = (id) => MODEL_NAMES[id] || id
const cleanText = (text) => String(text).replace(/\s*\(confidence [^)]*\)/gi, "").replace(/\bjev\b/gi, "MILO")

const TechnicalDetails = ({ decisions }) => {
    const [isOpen, setIsOpen] = useState(false)
    return (
        <div className="mt-2">
            <Button color="link" size="sm" className="p-0 text-muted font-size-12" onClick={() => setIsOpen(!isOpen)}>
                <i className={`bx ${isOpen ? "bx-chevron-down" : "bx-chevron-right"} align-middle`} /> Technical details (staff only)
            </Button>
            <Collapse isOpen={isOpen}>
                <div className="bg-light rounded p-2 mt-1 font-size-12">
                    {decisions.map(decision => {
                        const tech = decision.technical || {}
                        return (
                            <div key={decision.id} className="mb-2">
                                <div className="fw-semibold">
                                    <span className="text-capitalize">{decision.role}</span> · {modelName(tech.model_id)}
                                    {tech.model_version ? ` · version ${cleanText(tech.model_version)}` : ""}
                                </div>
                                {tech.explanation && <div><span className="text-muted">Model note:</span> {cleanText(tech.explanation)}</div>}
                                {tech.validation_detail && <div><span className="text-muted">Guardrail:</span> {cleanText(tech.validation_detail)}</div>}
                                {tech.error && <div className="text-danger"><span className="text-muted">Error:</span> {cleanText(tech.error)}</div>}
                                {(tech.source_notes || []).map((note, i) => (
                                    <div key={i}><span className="text-muted">{cleanText(note.source)}:</span> {cleanText(note.note)}</div>
                                ))}
                                {tech.latency_ms !== undefined && tech.latency_ms !== null && (
                                    <div className="text-muted">
                                        {tech.latency_ms} ms · {tech.input_tokens ?? "—"} in / {tech.output_tokens ?? "—"} out tokens · ${Number(tech.cost_usd || 0).toFixed(4)}
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>
            </Collapse>
        </div>
    )
}

const DecisionLine = ({ label, decision }) => {
    if (!decision) return null
    const failed = decision.validation_status === "error"
    const rejected = decision.validation_status === "rejected"
    return (
        <div className="d-flex font-size-13">
            <span className="text-muted" style={{ width: 90 }}>{label}</span>
            {failed && <span className="text-danger">Call failed — no suggestion</span>}
            {rejected && <span className="text-warning">Answer could not be used — no suggestion</span>}
            {!failed && !rejected && (
                <span>
                    <span className="fw-semibold">{formatDial(decision.dial_percent)}</span>
                    {decision.validation_status === "clamped" && <span className="badge badge-soft-info ms-1">Limited</span>}
                </span>
            )}
        </div>
    )
}

const TimelineItem = ({ cycle }) => {
    const primary = cycle.decisions.find(d => d.role === "primary")
    const status = MILO_STATUSES[getMiloStatus(cycle.decision_type, primary?.dial_percent)]
    const comparator = cycle.decisions.find(d => d.role === "comparator")
    const { pacing } = cycle
    const time = formatLocalTime(cycle.checked_at, pacing?.timezone, true)

    if (cycle.outcome === "reused") {
        return (
            <li className="milo-timeline-item milo-timeline-item--muted">
                <span className="milo-timeline-dot bg-light" />
                <div className="text-muted font-size-12">{time} · No change since the last check — last decision kept</div>
            </li>
        )
    }

    const reasonCode = primary?.reason_code || cycle.hold?.reason_code
    return (
        <li className="milo-timeline-item">
            <span className={`milo-timeline-dot ${status.dot}`} />
            <div className="d-flex align-items-center flex-wrap gap-2 mb-1">
                <span className="fw-semibold font-size-13">{time}</span>
                <DecisionBadge type={cycle.decision_type} dial={primary?.dial_percent} />
            </div>
            {reasonCode && <div className="mb-1">{getReasonText(reasonCode)}</div>}
            {cycle.decision_type === "platform_hold" && (
                <div className="text-muted font-size-12 mb-1">MILO was not asked: the platform held the price.</div>
            )}
            <DecisionLine label="MILO" decision={primary} />
            <DecisionLine label="Simple rule" decision={comparator} />
            <div className="text-muted font-size-12 mt-1">
                {pacing?.has_cap
                    ? <>Cap used {formatShare(pacing.used_share)} · expected {formatShare(pacing.day_elapsed_share)} · pace {formatRatio(pacing.pace_ratio)}</>
                    : <>No cap</>}
                {" · "}price {formatMoney(cycle.prices?.current_price)}
                {cycle.prices?.suggested_price !== null && cycle.prices?.suggested_price !== undefined && (
                    <span className="fst-italic"> → {formatMoney(cycle.prices.suggested_price)} (estimate)</span>
                )}
            </div>
            <TechnicalDetails decisions={cycle.decisions} />
        </li>
    )
}

const MiloTimeline = ({ cycles }) => {
    if (!cycles?.length) {
        return <div className="text-center text-muted py-5">No checks in the last 7 days.</div>
    }
    return (
        <ul className="milo-timeline list-unstyled mb-0">
            {cycles.map(cycle => <TimelineItem key={cycle.cycle_id} cycle={cycle} />)}
        </ul>
    )
}

export default MiloTimeline
